import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Trash2, CornerDownRight, MessageSquare, Loader2, X, Pencil, Check } from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';

const MAX_DEPTH = 4;

export default function EntryCard({
  node,
  depth,
  isAuthenticated,
  isAdmin,
  currentUserId,
  isModerator,
  onDelete,
  onReplySubmit,
  onEdit,
  activeReplyId,
  setActiveReplyId,
}) {
  const [replyName, setReplyName] = useState('');
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(node.message);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const isReplying = activeReplyId === node.id;

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyName.trim() || !replyText.trim()) return;
    setSubmitting(true);
    setLocalError('');
    try {
      await onReplySubmit({ sender: replyName.trim(), message: replyText.trim(), parent_id: node.id });
      setReplyName(''); setReplyText(''); setActiveReplyId(null);
    } catch (err) {
      setLocalError(formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Reply failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editText.trim() || editText === node.message) { setEditing(false); return; }
    setEditSubmitting(true);
    setLocalError('');
    try {
      await onEdit(node.id, editText.trim());
      setEditing(false);
    } catch (err) {
      setLocalError(formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Edit failed.');
    } finally {
      setEditSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="space-y-3"
    >
      <div
        className={`p-5 glass-card rounded-xl border space-y-3 relative ${node.isSystemSignal ? 'border-[#39ff14]/25 bg-emerald-500/5' : 'border-slate-850'}`}
        data-testid={`guestbook-entry-${node.id}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-black text-xs text-slate-100 uppercase tracking-wide">{node.sender}</span>
              {node.isSystemSignal && (
                <span className="bg-[#39ff14]/10 border border-[#39ff14]/30 text-[#39ff14] text-[8px] font-mono font-black py-0.5 px-2 rounded">SYS_INTEGRATOR</span>
              )}
              {node.parent_id && (
                <span className="font-mono text-[8px] text-[#9d4edd] bg-[#9d4edd]/10 border border-[#9d4edd]/25 px-1.5 py-0.5 rounded uppercase tracking-widest">Reply</span>
              )}
            </div>
            <div className="flex items-center gap-2 font-mono text-[9px] text-slate-500 flex-wrap">
              <span className="text-[#00f0ff]">{node.hash}</span>
              <span>|</span>
              <span title={isAuthenticated && node.ip_full ? 'Full IP visible to authenticated viewers' : 'Anonymous viewers see a masked IP'}>
                IP: <span className={isAuthenticated && node.ip_full ? 'text-amber-300' : ''}>
                  {isAuthenticated && node.ip_full ? node.ip_full : (node.ipPlaceholder || '127.x.x.x')}
                </span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 font-mono text-[8px] text-slate-500 uppercase shrink-0">
            <span>{new Date(node.timestamp).toLocaleTimeString()}</span>
            {node.edited_at && <span className="text-[#9d4edd]" title={`Edited ${new Date(node.edited_at).toLocaleString()}`}>· edited</span>}
            {isAuthenticated && !node.isSystemSignal && (isAdmin || (currentUserId && node.user_id === currentUserId)) && (
              <button
                onClick={() => { setEditing((e) => !e); setEditText(node.message); }}
                className={`p-1 rounded border transition-all cursor-pointer ${editing ? 'bg-[#00f0ff]/20 border-[#00f0ff]/40 text-[#00f0ff]' : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-[#00f0ff] hover:border-[#00f0ff]/40'}`}
                data-testid={`btn-edit-${node.id}`}
                title={editing ? 'Cancel edit' : 'Edit comment'}
              >
                {editing ? <X className="w-3 h-3" /> : <Pencil className="w-3 h-3" />}
              </button>
            )}
            {isAdmin && !node.isSystemSignal && (
              <button
                onClick={() => onDelete(node.id)}
                className="p-1 rounded bg-rose-500/10 hover:bg-rose-500 border border-rose-500/20 text-rose-400 hover:text-white transition-all cursor-pointer"
                data-testid={`btn-delete-${node.id}`}
                title="Purge footprint (admin only)"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {editing ? (
          <form onSubmit={handleEditSubmit} className="space-y-2" data-testid={`edit-form-${node.id}`}>
            <textarea
              required
              maxLength={256}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-slate-950 border border-[#00f0ff]/40 font-sans text-xs rounded focus:outline-none focus:border-[#00f0ff] transition-all text-white resize-none font-light"
              data-testid={`edit-text-${node.id}`}
            />
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={editSubmitting || !editText.trim()}
                className="px-3 py-1.5 bg-[#00f0ff] hover:bg-[#00d0ee] disabled:opacity-40 text-slate-950 font-mono font-bold text-[10px] uppercase tracking-widest rounded cursor-pointer transition-all flex items-center gap-1.5"
                data-testid={`btn-save-edit-${node.id}`}
              >
                {editSubmitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : <><Check className="w-3 h-3" /> Save</>}
              </button>
              <button
                type="button"
                onClick={() => { setEditing(false); setEditText(node.message); }}
                className="px-3 py-1.5 bg-slate-900 border border-slate-800 text-slate-400 hover:text-white font-mono font-bold text-[10px] uppercase tracking-widest rounded cursor-pointer"
              >
                Cancel
              </button>
              {localError && <span className="text-[10px] font-mono text-rose-400">! {localError}</span>}
            </div>
          </form>
        ) : (
          <p className={`text-xs tracking-wide leading-relaxed font-light ${node.isSystemSignal ? 'font-mono text-[#39ff14] text-[11px]' : 'text-slate-300'}`}>
            {node.message}
          </p>
        )}

        <div className="flex items-center gap-3 pt-1 border-t border-slate-900/60">
          {depth < MAX_DEPTH && !node.isSystemSignal && (
            <button
              onClick={() => setActiveReplyId(isReplying ? null : node.id)}
              className={`font-mono text-[10px] uppercase tracking-wider flex items-center gap-1.5 transition-all cursor-pointer ${isReplying ? 'text-[#00f0ff]' : 'text-slate-500 hover:text-[#00f0ff]'}`}
              data-testid={`btn-reply-${node.id}`}
            >
              {isReplying ? <X className="w-3 h-3" /> : <CornerDownRight className="w-3 h-3" />}
              <span>{isReplying ? 'Cancel' : 'Reply'}</span>
            </button>
          )}
          {node.children.length > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3" />
              <span>{node.children.length} {node.children.length === 1 ? 'reply' : 'replies'}</span>
            </span>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isReplying && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleReply}
            className="ml-4 md:ml-8 pl-4 border-l-2 border-[#00f0ff]/40 space-y-2 overflow-hidden"
            data-testid={`reply-form-${node.id}`}
          >
            <input
              type="text"
              required
              maxLength={32}
              value={replyName}
              onChange={(e) => setReplyName(e.target.value)}
              placeholder="Your name"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-850 font-mono text-xs rounded focus:outline-none focus:border-[#00f0ff] transition-all text-white placeholder:text-slate-600"
              data-testid={`reply-name-${node.id}`}
            />
            <textarea
              required
              maxLength={256}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`Reply to ${node.sender}...`}
              rows={2}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-850 font-sans text-xs rounded focus:outline-none focus:border-[#00f0ff] transition-all text-white placeholder:text-slate-600 resize-none font-light"
              data-testid={`reply-text-${node.id}`}
            />
            {localError && <div className="text-[10px] font-mono text-rose-400">! {localError}</div>}
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={submitting || !replyName.trim() || !replyText.trim()}
                className="px-3 py-1.5 bg-[#00f0ff] hover:bg-[#00d0ee] disabled:opacity-40 text-slate-950 font-mono font-bold text-[10px] uppercase tracking-widest rounded cursor-pointer transition-all flex items-center gap-1.5"
                data-testid={`btn-send-reply-${node.id}`}
              >
                {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : <><Send className="w-3 h-3" /> Post Reply</>}
              </button>
              <span className="font-mono text-[9px] text-slate-600">Rate-limited 30s/IP</span>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {node.children.length > 0 && (
        <div className="ml-4 md:ml-8 pl-4 border-l-2 border-slate-900 space-y-3" data-testid={`replies-of-${node.id}`}>
          {node.children.map((child) => React.createElement(EntryCard, {
            key: child.id,
            node: child,
            depth: depth + 1,
            isAuthenticated,
            isAdmin,
            currentUserId,
            isModerator,
            onDelete,
            onReplySubmit,
            onEdit,
            activeReplyId,
            setActiveReplyId,
          }))}
        </div>
      )}
    </motion.div>
  );
}
